import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaCheck,
  FaChevronRight,
  FaHeart,
  FaList,
  FaRegHeart,
  FaSearch,
  FaShoppingCart,
  FaSlidersH,
  FaTh,
  FaTimes,
} from "react-icons/fa";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";
import "../css/shop.scss";

const priceOf = (product) => Number.parseFloat(String(product.price).replace(/,/g, "")) || 0;

const CatalogCard = ({ product, view, onQuickView }) => {
  const { addToCart, favorites, toggleFavorite } = useContext(CartContext);
  const [added, setAdded] = useState(false);
  const isFavorite = favorites.some((item) => item.id === product.id);
  const comparePrice = (priceOf(product) * 1.18).toFixed(2);

  const handleCart = () => {
    addToCart(product.id, 1, product.size?.[0] || "", product.colors?.[0] || "");
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1300);
  };

  return (
    <article className={`catalog-card catalog-card--${view}`}>
      <div className="catalog-card__visual">
        <Link to={`/product/${product.id}`} aria-label={`View ${product.name}`}>
          <img
            className="catalog-card__image catalog-card__image--primary"
            src={product.images?.[0] || product.image}
            alt={product.name}
            loading="lazy"
          />
          {product.images?.[1] && (
            <img
              className="catalog-card__image catalog-card__image--secondary"
              src={product.images[1]}
              alt=""
              loading="lazy"
            />
          )}
        </Link>
        <span className="catalog-card__badge">SALE</span>
        <div className="catalog-card__floating-actions">
          <button
            type="button"
            className={isFavorite ? "is-active" : ""}
            onClick={() => toggleFavorite(product)}
            aria-label={isFavorite ? "Remove from wishlist" : "Add to wishlist"}
            title={isFavorite ? "Remove from wishlist" : "Add to wishlist"}
          >
            {isFavorite ? <FaHeart /> : <FaRegHeart />}
          </button>
          <button
            type="button"
            onClick={() => onQuickView(product)}
            aria-label="Quick view"
            title="Quick view"
          >
            <FaSearch />
          </button>
        </div>
      </div>

      <div className="catalog-card__body">
        <Link to={`/product/${product.id}`} className="catalog-card__name">
          {product.name}
        </Link>
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`} className="catalog-card__category">
          {product.category}
        </Link>
        <div className="catalog-card__rating" aria-label="Not yet rated">
          <span>★★★★★</span><small>No reviews</small>
        </div>
        <p className="catalog-card__description">{product.description?.[0]}</p>
        <div className="catalog-card__buy-row">
          <div className="catalog-card__price">
            <del>${comparePrice}</del>
            <strong>${priceOf(product).toFixed(2)}</strong>
          </div>
          <button type="button" className={added ? "is-added" : ""} onClick={handleCart}>
            {added ? <FaCheck /> : <FaShoppingCart />}
            <span>{added ? "ADDED" : "ADD TO CART"}</span>
          </button>
        </div>
      </div>
    </article>
  );
};

function Shop({ isWishlist = false }) {
  const { favorites, toggleFavorite, addToCart } = useContext(CartContext);
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const searchQuery = params.get("search") || "";
  const requestedCategory = params.get("category") || "All products";
  const [category, setCategory] = useState(requestedCategory);
  const [sortBy, setSortBy] = useState("featured");
  const [view, setView] = useState("grid");
  const [maxPrice, setMaxPrice] = useState(140);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [quickQuantity, setQuickQuantity] = useState(1);

  useEffect(() => {
    setCategory(requestedCategory);
  }, [requestedCategory]);

  useEffect(() => {
    if (!quickViewProduct) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setQuickViewProduct(null);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [quickViewProduct]);

  const categories = useMemo(() => {
    return [...new Set(productData.map((product) => product.category))].map((name) => ({
      name,
      count: productData.filter((product) => product.category === name).length,
      image: productData.find((product) => product.category === name)?.image,
    }));
  }, []);

  const products = useMemo(() => {
    const source = isWishlist ? favorites : productData;
    const query = searchQuery.trim().toLowerCase();
    const filtered = source.filter((product) => {
      const matchesSearch = !query || `${product.name} ${product.category}`.toLowerCase().includes(query);
      const matchesCategory = isWishlist || category === "All products" || product.category === category;
      return matchesSearch && matchesCategory && priceOf(product) <= maxPrice;
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === "price-low") return priceOf(a) - priceOf(b);
      if (sortBy === "price-high") return priceOf(b) - priceOf(a);
      if (sortBy === "name") return a.name.localeCompare(b.name);
      return a.id - b.id;
    });
  }, [category, favorites, isWishlist, maxPrice, searchQuery, sortBy]);

  const openQuickView = (product) => {
    setQuickViewProduct(product);
    setQuickQuantity(1);
  };

  const addQuickViewToCart = () => {
    if (!quickViewProduct) return;
    addToCart(
      quickViewProduct.id,
      quickQuantity,
      quickViewProduct.size?.[0] || "",
      quickViewProduct.colors?.[0] || ""
    );
    setQuickViewProduct(null);
  };

  if (isWishlist) {
    return (
      <main className="wishlist-page">
        <section className="catalog-hero catalog-hero--compact" style={{ backgroundImage: "linear-gradient(90deg, rgba(12, 31, 43, .9), rgba(12, 31, 43, .42)), url(/main/m2.webp)" }}>
          <div className="catalog-container">
            <p><Link to="/">Home</Link><FaChevronRight />Wishlist</p>
            <h1>Wishlist</h1>
          </div>
        </section>

        <section className="catalog-container wishlist-content">
          {favorites.length === 0 ? (
            <div className="wishlist-empty">
              <FaRegHeart />
              <h2>This wishlist is empty.</h2>
              <p>You don’t have any products in the wishlist yet. Discover our best electronics and save your favorites here.</p>
              <Link to="/shop">RETURN TO SHOP</Link>
            </div>
          ) : (
            <>
              <div className="wishlist-heading">
                <div>
                  <span>{favorites.length} SAVED {favorites.length === 1 ? "ITEM" : "ITEMS"}</span>
                  <h2>Products you love</h2>
                </div>
                <Link to="/shop">Continue shopping</Link>
              </div>
              <div className="wishlist-grid">
                {favorites.map((product) => (
                  <div className="wishlist-card-wrap" key={product.id}>
                    <button type="button" onClick={() => toggleFavorite(product)} aria-label={`Remove ${product.name}`}>
                      <FaTimes />
                    </button>
                    <CatalogCard product={product} view="grid" onQuickView={openQuickView} />
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
        {quickViewProduct && (
          <QuickView
            product={quickViewProduct}
            quantity={quickQuantity}
            setQuantity={setQuickQuantity}
            onClose={() => setQuickViewProduct(null)}
            onAdd={addQuickViewToCart}
          />
        )}
      </main>
    );
  }

  return (
    <main className="shop-page">
      <section className="catalog-hero" style={{ backgroundImage: "linear-gradient(90deg, rgba(12, 31, 43, .9), rgba(12, 31, 43, .42)), url(/main/m2.webp)" }}>
        <div className="catalog-container">
          <p><Link to="/">Home</Link><FaChevronRight />Shop</p>
          <h1>{searchQuery ? `Search results for “${searchQuery}”` : "Shop"}</h1>
          <span>Quality electronics selected for everyday work, listening and play.</span>
        </div>
      </section>

      <section className="catalog-categories catalog-container" aria-label="Product categories">
        {categories.map((item) => (
          <button
            type="button"
            key={item.name}
            className={category === item.name ? "is-active" : ""}
            onClick={() => setCategory(item.name)}
          >
            <span><img src={item.image} alt="" /></span>
            <strong>{item.name}</strong>
            <small>{item.count} products</small>
          </button>
        ))}
      </section>

      <section className="catalog-container shop-layout">
        <aside className="shop-sidebar">
          <div className="shop-filter-title"><FaSlidersH /><h2>Filter products</h2></div>
          <div className="shop-filter-group">
            <h3>Product categories</h3>
            <button type="button" className={category === "All products" ? "is-active" : ""} onClick={() => setCategory("All products")}>
              <span>All products</span><small>{productData.length}</small>
            </button>
            {categories.map((item) => (
              <button type="button" key={item.name} className={category === item.name ? "is-active" : ""} onClick={() => setCategory(item.name)}>
                <span>{item.name}</span><small>{item.count}</small>
              </button>
            ))}
          </div>
          <div className="shop-filter-group shop-price-filter">
            <h3>Filter by price</h3>
            <input
              type="range"
              min="50"
              max="140"
              value={maxPrice}
              onChange={(event) => setMaxPrice(Number(event.target.value))}
              aria-label="Maximum price"
            />
            <div><span>Price: $50 — ${maxPrice}</span><strong>{products.length} found</strong></div>
          </div>
          <div className="shop-help-card">
            <span>NEED HELP CHOOSING?</span>
            <h3>Find the right setup</h3>
            <p>Tell us how you work or play and our team will point you in the right direction.</p>
            <Link to="/contact">CONTACT US</Link>
          </div>
        </aside>

        <div className="shop-products">
          <div className="shop-toolbar">
            <p>Showing <strong>{products.length}</strong> of {productData.length} results</p>
            <div>
              <label>
                <span>Sort by:</span>
                <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                  <option value="featured">Default sorting</option>
                  <option value="price-low">Price: low to high</option>
                  <option value="price-high">Price: high to low</option>
                  <option value="name">Name</option>
                </select>
              </label>
              <button type="button" className={view === "grid" ? "is-active" : ""} onClick={() => setView("grid")} aria-label="Grid view"><FaTh /></button>
              <button type="button" className={view === "list" ? "is-active" : ""} onClick={() => setView("list")} aria-label="List view"><FaList /></button>
            </div>
          </div>

          {products.length ? (
            <div className={`catalog-grid catalog-grid--${view}`}>
              {products.map((product) => (
                <CatalogCard key={product.id} product={product} view={view} onQuickView={openQuickView} />
              ))}
            </div>
          ) : (
            <div className="shop-no-results">
              <FaSearch />
              <h2>No products were found</h2>
              <p>Try another category, a higher price range, or clear your search.</p>
              <Link to="/shop">VIEW ALL PRODUCTS</Link>
            </div>
          )}
        </div>
      </section>

      {quickViewProduct && (
        <QuickView
          product={quickViewProduct}
          quantity={quickQuantity}
          setQuantity={setQuickQuantity}
          onClose={() => setQuickViewProduct(null)}
          onAdd={addQuickViewToCart}
        />
      )}
    </main>
  );
}

const QuickView = ({ product, quantity, setQuantity, onClose, onAdd }) => (
  <div className="catalog-quickview" role="presentation" onMouseDown={onClose}>
    <div className="catalog-quickview__dialog" role="dialog" aria-modal="true" aria-label={`${product.name} quick view`} onMouseDown={(event) => event.stopPropagation()}>
      <button type="button" className="catalog-quickview__close" onClick={onClose} aria-label="Close quick view"><FaTimes /></button>
      <div className="catalog-quickview__image"><img src={product.images?.[0] || product.image} alt={product.name} /></div>
      <div className="catalog-quickview__content">
        <span>{product.category}</span>
        <h2>{product.name}</h2>
        <strong>${priceOf(product).toFixed(2)}</strong>
        <p>{product.description?.[0]}</p>
        <div className="catalog-quickview__stock"><FaCheck /> In stock — ready to ship</div>
        <div className="catalog-quickview__buy">
          <div>
            <button type="button" onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</button>
            <span>{quantity}</span>
            <button type="button" onClick={() => setQuantity(quantity + 1)}>+</button>
          </div>
          <button type="button" onClick={onAdd}>ADD TO CART</button>
        </div>
        <Link to={`/product/${product.id}`} onClick={onClose}>VIEW FULL DETAILS <FaChevronRight /></Link>
      </div>
    </div>
  </div>
);

export default Shop;
